<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, ref, reactive, computed } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { AppScreens } from "../../Enums/ChoiceEnums";
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { IQuickPayDemographicsSummaryResult, IQuickPayRecallItem, QuickPayRecallStatus } from '../../lib/Models/QuickPayModel';
    import { ModalStyles, popAlert } from "../../lib/Notification";
    import { IconTypes, DisplayStyle  } from "../../Enums/UiEnums";
    import { openWindowFromVue } from '../../lib/Models/CommonItemsModel';  
    import { IToolbar, ToolbarStyle, ToolbarBackground, ToolbarPadding } from "../../lib/Toolbar";
    import { ActionButtonStyle } from "../../lib/Types/Types"; 
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import Icon from "../../components/Icon.vue"
    import Toolbar from "../../components/Toolbar.vue"
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import * as StringFormat from "./../../lib/StringFormat";
    import Currency from '../../components/Formatting/Currency.vue';

    const windowHeight = ref<number>();
    const privilegesStore = usePrivilegesStore(initPinia());

    const dataClass = "col-sm label-isalus-small";
    const dataCopayClass = "col-sm label-isalus-small text-success";

    const labelClass = "pe-0 label-isalus-small fw-bold";
    const labelSmallClass = `labelSmallWidth ${labelClass}`;
    const labelMediumClass = `labelMediumWidth ${labelClass}`;
    const labelLargeClass = `labelLargeWidth ${labelClass}`;

    const props = defineProps<{
        patientId: number,
        patientEid: number,
        appointmentId?: number,
        scheduleDate?: string,
        tabId: number,
        validationExists?: boolean,
        validationMessage?: string
    }>();

    const formData = reactive<{
        result?: IQuickPayDemographicsSummaryResult,
        validationMessage: string[],
        useOldPatientAppointments: boolean
    }>({
        validationMessage: [],
        useOldPatientAppointments: true
    });

    const settingConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: "", label: "", tooltip:"Refresh the Patient Summary.", action: ActionButtonStyle.Refresh, performAction: () => refresh()},
        ]
    });    

    async function refresh() {
        privilegesStore.getScreens([AppScreens.PatientSetup, AppScreens.PatientAppointments, AppScreens.PaymentPlan, AppScreens.TransactionHistory]);
        formData.result = await ChoiceApi.quickPay.DemographicsSummary(props.patientId);
    }
    refresh();

    onMounted(async () => {
        if (props.validationMessage) {
            formData.validationMessage = props.validationMessage.split("|");
        }
        const useOldScreen = await ChoiceApi.user.useScreen('PatientAppointments');
        formData.useOldPatientAppointments = useOldScreen?.use ?? true;
        windowResize();
    });

    const maxResponsibleParties = computed<number>(() => formData.result?.responsiblePartyList.length ?? 0 > 2 ? 2 : formData.result?.responsiblePartyList.length ?? 0);
    const addResponsibleParties = computed<number>(() => (formData.result?.responsiblePartyList.length ?? 0) - 2);
    
    const maxInsured = computed<number>(() => formData.result?.insuredList.length ?? 0 > 2 ? 2 : formData.result?.insuredList.length ?? 0);
    const addInsured = computed<number>(() => (formData.result?.insuredList.length ?? 0) - 2);
    const slidingFeeText = computed<string>(() => (formData.result?.slidingFee ?? "").replaceAll("@sFMT@", "<span class='isalus-section-header fst-italic'>").replaceAll("@eFMT@","</span>"));
    const validationStyle = computed<string>(() => (`label-isalus-small ${props.validationExists ? "text-danger" : ""}`));

    function windowResize() {
        windowHeight.value = window.innerHeight - 100;
    }

    function scheduleRecall(recallItem: IQuickPayRecallItem) {
        MessageLib.Scheduler.loadPatientResourceDate(
            {patientEid: recallItem.patientEid, 
             resourceId: recallItem.resourceId, 
             scheduleDate: recallItem.dueDate.substring(0,10)});
    }

    async function cancelRecall(recallItem: IQuickPayRecallItem) {
        const response = await ChoiceApi.recall.cancel(recallItem.recallReminderId);
        if (!response.passed) {
            popAlert(response.message ?? "Failed to cancel the recall item", "Cancel", ModalStyles.warning);
            return;
        }
        recallItem.recallStatusCode = QuickPayRecallStatus.Cancelled;
    }

    function openPatientSetup(tab: string) {
        if (privilegesStore.screensWithReadPrivileges[AppScreens.PatientSetup]) {
            const windowId = AppScreens.PatientSetup;
            const windowUrl = `/officemd/screens/WindowSetup.htm?WT=Patient Setup&Qualifier=patient&WindowID=${props.patientId}&tab=${tab}`;
            openWindowFromVue(windowId, windowUrl, { noTimestamp: "Y" });
        }
        else {
            popAlert("You don't have access to Patient Setup. Please contact your System Administrator.","Open Patient Setup",ModalStyles.info);
        }
    }

    function openPatientAppointments() {
        if (privilegesStore.screensWithReadPrivileges[AppScreens.PatientAppointments]) {
            const windowId = AppScreens.PatientAppointments;
            let windowUrl;
            if (formData.useOldPatientAppointments)
                windowUrl = `/officemd/screens/AppointmentPatient.asp?EID=${props.patientEid}`;
            else
                windowUrl = `/OfficeMd/App/Schedule/PatientAppointments/${props.patientEid}`;
            openWindowFromVue(windowId, windowUrl, { noTimestamp: "Y" });
        }
        else {
            popAlert("You don't have access to Patient Appointments. Please contact your System Administrator.","Open Patient Appointments",ModalStyles.info);
        }        
    }

    function openTransactionHistory() {
        if (privilegesStore.screensWithReadPrivileges[AppScreens.TransactionHistory]) {
            const windowId = AppScreens.TransactionHistory;
            const windowUrl = `/officemd/screens/transactionHistory.asp?PID=${props.patientId}`;
            openWindowFromVue(windowId, windowUrl, { noTimestamp: "Y" });
        }
        else {
            popAlert("You don't have access to Patient Transaction History. Please contact your System Administrator.","Open Patient Transaction History",ModalStyles.info);
        }        
    }

    function openPaymentPlan(paymentPlanId: number) {
        if (privilegesStore.screensWithReadPrivileges[AppScreens.PaymentPlan]) {
            const windowId = AppScreens.PaymentPlan;
            const windowUrl = `/officemd/App/billing/PaymentPlan/${paymentPlanId}`;
            openWindowFromVue(windowId, windowUrl, { noTimestamp: "Y" });
        }
        else {
            popAlert("You don't have access to Payment Plan. Please contact your System Administrator.","Open Payment Plan",ModalStyles.info);
        }      
    }

</script>

<style scoped>
    .fixedLabelWidth {
        width: 70px;
    }
    .fixedInsuredLabelWidth {
        width: 120px;
    }
    .printQueue {
        height: 500px;
    }
    .labelSmallWidth {
        width: 50px;
    }
    .labelMediumWidth {
        width: 75px;
    }
    .labelLargeWidth {
        width: 100px;
    }
    .dataSmallWidth {
        width: 100px;
    }
    .dataMediumWidth {
        width: 200px;
    }
    .chartSection:hover {
        background-color: #fff6e6;
        cursor: pointer;
    }
    .chartSection {
        background-color: white;
    }
</style>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="row">
        <div class="col">
            <template v-if="props.validationMessage">
                <div class="row">
                    <div class="col">
                        <template v-if="props.validationExists">
                            <Icon  
                                :icon="IconTypes.Warning" 
                                :displayStyle="DisplayStyle.Warning" 
                                :class="'text-isalus-primary ms-2 fw-bold'"
                                :title="'Failed validation checks'"/>   
                        </template>
                        <template v-else>       
                            <Icon 
                                :icon="IconTypes.CheckMark" 
                                :displayStyle="DisplayStyle.Success" 
                                :class="'text-isalus-primary ms-2 fw-bold'"
                                :title="'Passed'"/>
                        </template>
                    </div>
                </div>
                <div class="row ps-3 mb-2">
                    <div :class="validationStyle" v-for="message in formData.validationMessage">
                        {{ message }}
                    </div>
                </div>
            </template>
        </div>
        <div class="col-auto pe-3">
            <Toolbar 
                :configuration="settingConfig" 
                :toolbarStyle="ToolbarStyle.outline" 
                :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                :toolbarBackground="ToolbarBackground.white"/> 
        </div>          
    </div>
    <template v-if="formData.result?.chart">
        <div class="chartSection" @click="openPatientSetup('Demographics')">
            <div class="row">
                <div class="col fw-bold label-isalus-medium isalus-section-header">
                    Chart {{ formData.result.chart.patientId }}
                </div>
            </div>
            <div class="row ps-3">
                <div :class="dataClass">
                    {{ formData.result.chart.address1 }}
                </div>
                <div :class="labelSmallClass">
                    Home
                </div>        
                <div :class="dataClass">
                    {{ formData.result.chart.phoneHome }}
                </div>
                <div :class="labelMediumClass">
                    PCP
                </div>        
                <div :class="dataClass">
                    {{ formData.result.chart.pcp }}
                </div>
                <div :class="labelMediumClass">
                    Employer
                </div>        
                <div :class="dataClass">
                    {{ formData.result.chart.employer }}
                </div>

                <div class="w-100"></div>

                <div :class="dataClass">
                    {{ formData.result.chart.address2 }}
                </div>
                <div :class="labelSmallClass">
                    Work
                </div>        
                <div :class="dataClass">
                    {{ formData.result.chart.phoneWork }}
                </div>
                <div :class="labelMediumClass">
                    {{ formData.result.chart.primaryIdType }}
                </div>        
                <div :class="dataClass">
                    {{ formData.result.chart.primaryId }}
                </div>
                <div :class="labelMediumClass">
                    Ethnicity
                </div>        
                <div :class="dataClass">
                    {{ formData.result.chart.ethnicity }}
                </div>

                <div class="w-100"></div>

                <div :class="dataClass">
                    {{ formData.result.chart.city }}, {{ formData.result.chart.state }} {{ formData.result.chart.zip }} {{ formData.result.chart.country }}
                </div>
                <div :class="labelSmallClass">
                    Other
                </div>        
                <div :class="dataClass">
                    {{ formData.result.chart.phoneOther }}
                </div>
                <div :class="labelMediumClass">
                    Marital
                </div>        
                <div :class="dataClass">
                    {{ formData.result.chart.maritalStatus }}
                </div>
                <div :class="labelMediumClass">
                    Race
                </div>        
                <div :class="dataClass">
                    {{ formData.result.chart.race }}
                </div>

                <div class="w-100"></div>

                <div :class="dataClass">
                    {{ formData.result.chart.email }}
                </div>
                <div :class="labelSmallClass">
                </div>        
                <div :class="dataClass">
                </div>
                <div :class="labelMediumClass">
                    Language
                </div>        
                <div :class="dataClass">
                    {{ formData.result.chart.language }}
                </div>
                <div :class="labelMediumClass">
                </div>        
                <div :class="dataClass">
                </div>
            </div>
        </div>
    </template>

    <template v-if="formData.result?.contactList.length ?? 0 > 0">
        <hr>
        <div class="chartSection" @click="openPatientSetup('Emergency Contact')">
            <div class="row" title="Emergency Contacts">
                <template v-for="(item, index) in formData.result?.contactList">
                    <div class="col-6">
                        <div class="row">
                            <div class="col fw-bold label-isalus-medium isalus-section-header">
                                {{ item.contactName }}
                            </div>
                        </div>
                        <div class="row ps-3">
                            <div :class="labelSmallClass">
                                Home
                            </div>        
                            <div :class="dataClass">
                                {{ item.phoneHome }}
                            </div>

                            <div class="w-100"></div>
                            <div :class="labelSmallClass">
                                Work
                            </div>        
                            <div :class="dataClass">
                                {{ item.phoneWork }}
                            </div>

                            <div class="w-100"></div>

                            <div :class="labelSmallClass">
                                Other
                            </div>        
                            <div :class="dataClass">
                                {{ item.phoneOther }}
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </template>

    <template v-if="formData.result?.insuredList.length ?? 0 > 0">
        <hr>
        <div class="chartSection" @click="openPatientSetup('Insurance')">
            <template v-for="(item, index) in formData.result?.insuredList">
                <template v-if="index < maxInsured">
                    <div :class="index > 0 ? 'row pt-1' : 'row'">
                        <div class="col fw-bold label-isalus-medium isalus-section-header">
                            {{ item.payerName }} - {{ item.payerPhone }}
                        </div> 
                    </div>
                    <div class="row ps-3">
                        <div :class="dataClass">
                            {{ item.insuranceType }}
                        </div>
                        <div :class="labelLargeClass">
                            Start Date
                        </div>        
                        <div :class="dataClass">
                            {{ item.startDate }}
                        </div>
                        <div :class="labelLargeClass">
                            Sig On File
                        </div>        
                        <div :class="dataClass">
                            {{ item.signatureOnFileDate }}
                        </div>

                        <div class="w-100"></div>

                        <div :class="dataCopayClass">
                            {{ item.copay }}
                        </div>
                        <div :class="labelLargeClass">
                            Member ID #
                        </div>        
                        <div :class="dataClass">
                            {{ item.idCode }}
                        </div>
                        <div :class="labelLargeClass">
                            Group Policy #
                        </div>        
                        <div :class="dataClass">
                            {{ item.groupPolicyNumber }}
                        </div>
                    </div>
                </template>
            </template>
            <div v-if="addInsured > 0" class="row pt-2">
                <div class="col label-isalus-small fst-italic" title="There are additional active insurances that are not displayed here.">
                    +{{ addInsured }} Active Insurance(s)
                </div>
            </div>
        </div>
    </template>

    <template v-if="formData.result?.caseList.length ?? 0 > 0">
        <hr>
        <div class="chartSection" @click="openPatientSetup('Case Management')">
            <div class="row">
                <div class="col fw-bold label-isalus-medium isalus-section-header">
                    Cases (Active)
                </div>
            </div>
            <div class="row ps-3" v-for="item in formData.result?.caseList">
                <div class="dataSmallWidth label-isalus-small">
                    {{ item.careDate }}
                </div>
                <div class="dataMediumWidth label-isalus-small">
                    {{ item.program }}
                </div>
                <div class="dataSmallWidth label-isalus-small">
                    {{ item.status }}
                </div>
                <div class="col label-isalus-small">
                    {{ item.caseManager }}
                </div>
            </div>
        </div>
    </template>

    <template v-if="formData.result?.employer">
        <hr>
        <div class="chartSection" @click="openPatientSetup('MML Self Schedule')">
            <div class="row">
                <div class="col fw-bold label-isalus-medium isalus-section-header">
                    {{ formData.result.employer.employerName}} ({{ formData.result.employer.employeeType}})
                </div>
            </div>
            <div class="row ps-3">
                <div :class="labelMediumClass">
                    Clinic Start
                </div>        
                <div class="dataSmallWidth label-isalus-small">
                    {{ formData.result.employer.clinicAccessStartDate }}
                </div>
                <div :class="labelMediumClass">
                    Health Plan
                </div>        
                <div :class="dataClass">
                    {{ formData.result.employer.healthPlan }}
                </div>

                <div class="w-100"></div>

                <div :class="labelMediumClass">
                    Clinic End
                </div>        
                <div class="dataSmallWidth label-isalus-small">
                    {{ formData.result.employer.clinicAccessEndDate }}
                </div>
                <div :class="labelMediumClass">
                    Sub Pop.
                </div>        
                <div :class="dataClass">
                    {{ formData.result.employer.population }}
                </div>

                <div class="w-100"></div>

                <div :class="labelMediumClass">
                    Registration
                </div>        
                <div class="dataSmallWidth label-isalus-small">
                    {{ formData.result.employer.registrationDate }}
                </div>
                <div :class="labelMediumClass">
                    Department
                </div>        
                <div :class="dataClass">
                    {{ formData.result.employer.department }}
                </div>

                <div class="w-100"></div>

                <div :class="labelMediumClass">
                    Wellness
                </div>        
                <div class="dataSmallWidth label-isalus-small">
                    {{ formData.result.employer.wellnessDate }}
                </div>
                <div :class="labelMediumClass">
                    Locality
                </div>        
                <div :class="dataClass">
                    {{ formData.result.employer.locality }}
                </div>
            </div>
        </div>
    </template>


    <template v-if="formData.result?.responsiblePartyList.length ?? 0 > 0">
        <hr>
        <div class="chartSection" @click="openPatientSetup('Responsible Party')">
            <div class="row" title="Responsible Party">
                <template v-for="(item, index) in formData.result?.responsiblePartyList">
                    <template v-if="index < maxResponsibleParties">
                        <div class="col-6">
                            <div class="row">
                                <div class="col fw-bold label-isalus-medium isalus-section-header">
                                    {{ item.respPartyName }}
                                </div>
                            </div>
                            <div class="row ps-3">
                                <div :class="dataClass">
                                    {{ item.address1 }}
                                </div>
                                <div :class="labelSmallClass">
                                    Home
                                </div>        
                                <div :class="dataClass">
                                    {{ item.phoneHome }}
                                </div>

                                <div class="w-100"></div>

                                <div :class="dataClass">
                                    {{ item.address2 }}
                                </div>
                                <div :class="labelSmallClass">
                                    Work
                                </div>        
                                <div :class="dataClass">
                                    {{ item.phoneWork }}
                                </div>

                                <div class="w-100"></div>

                                <div :class="dataClass">
                                    {{ item.city }}, {{ item.state }} {{ item.zip }} {{ item.country }}
                                </div>
                                <div :class="labelSmallClass">
                                    Other
                                </div>        
                                <div :class="dataClass">
                                    {{ item.phoneOther }}
                                </div>
                            </div>
                        </div>
                    </template>
                </template>
            </div>
            <div v-if="addResponsibleParties > 0" class="row pt-2">
                <div class="col label-isalus-small fst-italic" title="There are additional active responsible parties that are not displayed here">
                    +{{ addResponsibleParties }} Responsible Parties
                </div>
            </div>
        </div>
    </template>

    <hr>
    <div class="chartSection" @click="openPatientAppointments()">
        <div class="row">
            <div class="col fw-bold label-isalus-medium isalus-section-header">
                Next 3 Visits
            </div>
        </div>
        <template v-if="formData.result?.nextVisitList">
            <div class="row ps-3" v-for="item in formData.result.nextVisitList">
                <div :class="dataClass">
                    {{ item.appointmentDate }}({{ item.duration }}) 
                </div>
                <div :class="dataClass">
                    {{ item.resourceName }}
                </div>
                <div :class="dataClass">
                    {{ item.serviceLocationName }}
                </div>
                <div :class="dataClass">
                    {{ item.appointmentStatus }}
                </div>
                <div :class="dataClass">
                    {{ item.appointmentType }}
                </div>
            </div>
            <div class="row pt-1 ps-3" v-if="(formData.result.nextVisitList.length ?? 0) == 0">
                <div class="col label-isalus-small fst-italic">
                    Appointments do not exist
                </div>
            </div>
        </template>

        <div class="row mt-1">
            <div class="col fw-bold label-isalus-medium isalus-section-header">
                Previous 3 Visits
            </div>
        </div>
        <template v-if="formData.result?.previousVisitList">
            <div class="row ps-3" v-for="item in formData.result.previousVisitList">
                <div :class="dataClass">
                    {{ item.appointmentDate }}({{ item.duration }}) 
                </div>
                <div :class="dataClass">
                    {{ item.resourceName }}
                </div>
                <div :class="dataClass">
                    {{ item.serviceLocationName }}
                </div>
                <div :class="dataClass">
                    {{ item.appointmentStatus }}
                </div>
                <div :class="dataClass">
                    {{ item.appointmentType }}
                </div>
            </div>
            <div class="row pt-1 ps-3" v-if="(formData.result.previousVisitList.length ?? 0) == 0">
                <div class="col label-isalus-small fst-italic">
                    Appointments do not exist
                </div>
            </div>
        </template>
    </div>

    <template v-if="(formData.result?.recallNextList.length ?? 0) > 0 || (formData.result?.recallPreviousList.length ?? 0) > 0">
        <hr >
    </template>

    <template v-if="formData.result?.recallNextList.length ?? 0 > 0">
        <div class="row">
            <div class="col fw-bold label-isalus-medium isalus-section-header">
                Next 3 Recalls
            </div>
        </div>
        <div class="row ps-3" v-for="item in formData.result?.recallNextList">
            <div class="dataSmallWidth label-isalus-small mt-1">
                {{ item.dueDate }}
            </div>
            <div :class="[dataClass,'mt-1']">
                {{ item.resourceName }}
            </div>
            <div :class="[dataClass,'mt-1']">
                {{ item.appointmentType }}
            </div>
            <div :class="[dataClass,'mt-1']">
                {{ item.comments }}
            </div>
            <div class="col-sm">
                <span v-if="item.resourceId && item.recallStatusCode == QuickPayRecallStatus.Recall" @click="() => scheduleRecall(item)" class="isalus-link label-isalus-small">
                    Schedule
                </span>                 
                <span v-if="item.recallStatusCode == QuickPayRecallStatus.Cancelled" title="Recall was cancelled" class="label-isalus-small text-decoration-line-through">
                    Schedule
                </span>
            </div>
            <div class="col-sm">
                <span v-if="item.recallStatusCode == QuickPayRecallStatus.Recall" @click="() => cancelRecall(item)" class="isalus-link label-isalus-small">
                    Cancel
                </span>   
                <span v-if="item.recallStatusCode == QuickPayRecallStatus.Cancelled" title="Recall was cancelled" class="label-isalus-small text-decoration-line-through">
                    Cancel
                </span>
            </div>
        </div>
    </template>

    <template v-if="formData.result?.recallPreviousList.length ?? 0 > 0">
        <div class="row">
            <div class="col fw-bold label-isalus-medium isalus-section-header">
                Past 3 Recalls
            </div>
        </div>
        <div class="row ps-3" v-for="item in formData.result?.recallPreviousList">
            <div class="dataSmallWidth label-isalus-small mt-1">
                {{ item.dueDate }}
            </div>
            <div :class="[dataClass,'mt-1']">
                {{ item.resourceName }}
            </div>
            <div :class="[dataClass,'mt-1']">
                {{ item.appointmentType }}
            </div>
            <div :class="[dataClass,'mt-1']">
                {{ item.comments }}
            </div>
            <div class="col-sm">
                <span v-if="item.resourceId && item.recallStatusCode == QuickPayRecallStatus.Recall" @click="() => scheduleRecall(item)" class="isalus-link label-isalus-small">
                    Schedule
                </span>                 
                <span v-if="item.recallStatusCode == QuickPayRecallStatus.Cancelled" title="Recall was cancelled" class="label-isalus-small text-decoration-line-through">
                    Schedule
                </span>
            </div>
            <div class="col-sm">
                <span v-if="item.recallStatusCode == QuickPayRecallStatus.Recall" @click="() => cancelRecall(item)" class="isalus-link label-isalus-small">
                    Cancel
                </span>   
                <span v-if="item.recallStatusCode == QuickPayRecallStatus.Cancelled" title="Recall was cancelled" class="label-isalus-small text-decoration-line-through">
                    Cancel
                </span>
            </div>
        </div>
    </template>

    <template v-if="formData.result?.slidingFee">
        <hr>
        <div class="chartSection" @click="openPatientSetup('Sliding Fee')">
            <div class="row">
                <div class="col fw-bold label-isalus-medium isalus-section-header">
                    Sliding Fee
                </div>
            </div>
            <div class="row label-isalus-small">
                <div class="col-auto">
                    <div class="row ps-3">
                        <div class="col" v-html="slidingFeeText">
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </template>

    <template v-if="formData.result?.balance">
        <hr>
        <div class="chartSection" @click="openTransactionHistory()">
            <div class="row">
                <div class="col fw-bold label-isalus-medium isalus-section-header">
                    Balance: <Currency :value="+formData.result.balance.totalBalance"/>
                </div>
            </div>
            <div class="row label-isalus-small">
                <div class="col-auto">
                    <div class="row ps-3">
                        <div class="col">
                            {{formData.result.balance.label1 }}
                        </div>
                    </div>
                    <div class="row ps-3" v-if="formData.result.balance.label1">
                        <div class="col">
                            <Currency :value="+formData.result.balance.balance1"/>
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            {{formData.result.balance.label2 }}
                        </div>
                    </div>
                    <div class="row" v-if="formData.result.balance.label2">
                        <div class="col ps-0">
                            <Currency :value="+formData.result.balance.balance2"/>
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            {{StringFormat.ToMoneyStr(formData.result.balance.label3) }}
                        </div>
                    </div>
                    <div class="row" v-if="formData.result.balance.label3">
                        <div class="col ps-0">
                            <Currency :value="+formData.result.balance.balance3"/>
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            {{formData.result.balance.label4 }}
                        </div>
                    </div>
                    <div class="row" v-if="formData.result.balance.label4">
                        <div class="col ps-0">
                            <Currency :value="+formData.result.balance.balance4"/>
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            {{formData.result.balance.label5 }}
                        </div>
                    </div>
                    <div class="row" v-if="formData.result.balance.label5">
                        <div class="col ps-0">
                            <Currency :value="+formData.result.balance.balance5"/>
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            {{formData.result.balance.label6 }}
                        </div>
                    </div>
                    <div class="row" v-if="formData.result.balance.label6">
                        <div class="col ps-0">
                            <Currency :value="+formData.result.balance.balance6"/>
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            {{formData.result.balance.label7 }}
                        </div>
                    </div>
                    <div class="row" v-if="formData.result.balance.label7">
                        <div class="col ps-0">
                            <Currency :value="+formData.result.balance.balance7"/>
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            {{formData.result.balance.label8 }}
                        </div>
                    </div>
                    <div class="row" v-if="formData.result.balance.label8">
                        <div class="col ps-0">
                            <Currency :value="+formData.result.balance.balance8"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </template>

    <template v-if="formData.result?.paymentPlanList.length ?? 0 > 0">
        <hr>
        <template v-for="item in formData.result?.paymentPlanList">
            <div class="row">
                <div class="col-auto fw-bold label-isalus-medium isalus-section-header">
                    Payment Plan: {{ StringFormat.ToMoneyStr(item.remainingTotal) }}
                </div>
            </div>
            <div class="row label-isalus-small">
                <div class="col-auto">
                    <div class="row ps-3">
                        <div class="col">
                            ID
                        </div>
                    </div>
                    <div class="row ps-3">
                        <div class="col">
                            <span @click="openPaymentPlan(item.paymentPlanId)" class="isalus-link label-isalus-small">
                                {{ item.paymentPlanId }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            Total
                        </div>
                    </div>
                    <div class="row">
                        <div class="col ps-0">
                            {{StringFormat.ToMoneyStr(item.totalAmount) }}
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            Remaining
                        </div>
                    </div>
                    <div class="row">
                        <div class="col ps-0">
                            {{StringFormat.ToMoneyStr(item.remainingTotal) }}
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            Due
                        </div>
                    </div>
                    <div class="row">
                        <div class="col ps-0">
                            {{ item.nextDueDate }}
                        </div>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="row">
                        <div class="col ps-0">
                            Status
                        </div>
                    </div>
                    <div class="row">
                        <div class="col ps-0">
                            {{ item.status }}
                        </div>
                    </div>
                </div>
                <div class="col"></div>
            </div>
        </template> 
    </template>

    <template v-if="formData.result?.populationHealthList.length ?? 0 > 0">
        <hr>
        <div class="row">
            <div class="col fw-bold label-isalus-medium isalus-section-header">
                Population Health
            </div>
        </div>
        <div class="row ps-3" v-for="item in formData.result?.populationHealthList">
            <div :class="dataClass">
                {{ item.description }}
            </div>
            <div class="dataSmallWidth label-isalus-small">
                {{ item.loadDate }}
            </div>
            <div class="dataSmallWidth label-isalus-small">
                {{ item.contactDate }}
            </div>
            <div :class="dataClass">
                {{ item.contactMethod }}
            </div>
            <div :class="dataClass">
                {{ item.contactNote }}
            </div>
            <div :class="dataClass">
                {{ item.userId }}
            </div>
        </div>
    </template>

</template>

